import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BookOpen,
  Download,
  CheckCircle2,
  Clock,
  Sparkles,
  ArrowLeft,
  MessageCircle,
  ExternalLink,
  Smartphone,
  HelpCircle,
  Play,
} from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { tutorialsData } from '../data/tutorialsData';

export const KnowledgeCenter = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const videoPlayerRef = useRef(null);

  // State video yang aktif
  const [activeVideoId, setActiveVideoId] = useState(tutorialsData[0]?.id);
  const activeVideo =
    tutorialsData.find((t) => t.id === activeVideoId) || tutorialsData[0];

  const handleSelectVideo = (id) => {
    setActiveVideoId(id);
    if (videoPlayerRef.current) {
      videoPlayerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleCopyLink = () => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: 'Tautan Berhasil Disalin',
        description: 'Tautan halaman Knowledge Center telah disalin ke papan klip.',
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-background to-gold-400/10 text-foreground">
      {/* Header Navigasi Atas */}
      <header className="sticky top-0 z-40 bg-background/85 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
          <div
            className="flex items-center space-x-3 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <div className="w-10 h-10 bg-brand-700 rounded-lg flex items-center justify-center p-1.5 shadow-sm">
              <img
                src="/logo-ipl-removebg.webp"
                alt="Logo Sistem IPL"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <span className="text-xl sm:text-2xl font-bold text-brand-700 block leading-tight">
                Sistem IPL
              </span>
              <span className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Knowledge Center
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="hidden sm:inline-flex items-center gap-1 text-foreground/80 hover:text-brand-700"
            >
              <ArrowLeft size={16} />
              Beranda
            </Button>
            <Link to="/download">
              <Button
                variant="outline"
                size="sm"
                className="border-brand-300 text-brand-700 hover:bg-brand-50 gap-1.5"
              >
                <Download size={15} />
                Unduh APK
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Bagian Hero */}
      <section className="relative pt-10 sm:pt-14 pb-6 sm:pb-10 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="inline-flex items-center gap-2 bg-brand-100/90 text-brand-800 text-xs sm:text-sm font-semibold px-4 py-1.5 rounded-full mb-4 border border-brand-200 shadow-sm"
            >
              <BookOpen size={16} className="text-brand-700" />
              Pusat Edukasi & Panduan Pengguna
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-foreground tracking-tight leading-tight mb-4"
            >
              Knowledge Center <br />
              <span className="bg-gradient-to-r from-brand-700 to-brand-500 bg-clip-text text-transparent">
                Panduan Video Aplikasi Sistem IPL
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto"
            >
              Panduan visual resmi untuk mempermudah Anda dalam mengunduh, memasang, dan mengoperasikan
              aplikasi Sistem IPL di perangkat seluler Anda.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Konten Utama */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {/* Pilihan Video Tutorial (Switcher) */}
        {tutorialsData.length > 1 && (
          <div className="max-w-4xl mx-auto mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-border/80">
              <span className="text-xs font-bold text-foreground/75 uppercase tracking-wider">
                Pilih Topik Panduan Video:
              </span>
              <span className="text-xs text-muted-foreground">
                Tersedia {tutorialsData.length} video tutorial
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              {tutorialsData.map((tutorial) => {
                const isSelected = tutorial.id === activeVideo.id;
                return (
                  <button
                    key={tutorial.id}
                    onClick={() => handleSelectVideo(tutorial.id)}
                    className={`text-left p-4 rounded-2xl border transition-all duration-200 flex items-start justify-between gap-3 ${
                      isSelected
                        ? 'bg-brand-700 text-white border-brand-700 shadow-md shadow-brand-700/20 ring-2 ring-brand-600/30'
                        : 'bg-card text-foreground hover:bg-brand-50 hover:border-brand-300 border-border shadow-xs'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          isSelected
                            ? 'bg-white/20 text-white'
                            : 'bg-brand-100 text-brand-700'
                        }`}
                      >
                        <Play size={16} className="ml-0.5 fill-current" />
                      </div>
                      <div>
                        <h4 className="font-bold text-sm leading-snug">
                          {tutorial.shortTitle || tutorial.title}
                        </h4>
                        <p
                          className={`text-xs mt-1 line-clamp-1 ${
                            isSelected ? 'text-white/80' : 'text-muted-foreground'
                          }`}
                        >
                          {tutorial.brand} • {tutorial.duration}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Kartu Pemutar Video */}
        <section ref={videoPlayerRef} className="max-w-4xl mx-auto mb-16 scroll-mt-24">
          <div className="bg-card rounded-3xl border border-border shadow-xl overflow-hidden">
            <div className="p-4 sm:p-6 lg:p-8 bg-gradient-to-b from-brand-50/50 to-transparent">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Badge Tipe Panduan */}
                  <span className="inline-flex items-center gap-1.5 bg-gold-500/15 text-gold-700 font-bold text-xs px-3 py-1 rounded-full border border-gold-400/30">
                    <Sparkles size={13} className="text-gold-600" />
                    Panduan Video
                  </span>

                  {/* Badge 1: Android */}
                  <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-800 font-semibold text-xs px-3 py-1 rounded-full border border-emerald-200">
                    <Smartphone size={13} className="text-emerald-600" />
                    {activeVideo.platform || 'Android'}
                  </span>

                  {/* Badge 2: Topik / Brand */}
                  <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-800 font-semibold text-xs px-3 py-1 rounded-full border border-blue-200">
                    {activeVideo.brand}
                  </span>

                  {/* Badge Durasi */}
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock size={13} />
                    {activeVideo.duration}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyLink}
                    className="text-xs h-8 border-border"
                  >
                    Salin Tautan
                  </Button>
                  <a
                    href={activeVideo.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-semibold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1"
                  >
                    Buka Berkas Asli
                    <ExternalLink size={13} />
                  </a>
                </div>
              </div>

              <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground mb-3">
                {activeVideo.title}
              </h2>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
                {activeVideo.description}
              </p>

              {/* Kotak Pemutar Video */}
              <div className="relative rounded-2xl overflow-hidden bg-slate-950 shadow-2xl border border-slate-800 aspect-video flex items-center justify-center">
                <video
                  key={activeVideo.videoUrl}
                  controls
                  controlsList="nodownload"
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-contain bg-black"
                >
                  <source src={activeVideo.videoUrl} type="video/mp4" />
                  Peramban Anda tidak mendukung pemutaran video HTML5.
                </video>
              </div>

              {/* Tahapan Utama */}
              {activeVideo.highlights && activeVideo.highlights.length > 0 && (
                <div className="mt-8 pt-6 border-t border-border">
                  <h3 className="text-sm sm:text-base font-bold text-foreground mb-3 flex items-center gap-2">
                    <CheckCircle2 size={18} className="text-brand-600" />
                    Tahapan Utama Dalam Panduan Ini:
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {activeVideo.highlights.map((point, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2.5 bg-background/80 p-3 rounded-xl border border-border/80 text-xs sm:text-sm text-foreground/80"
                      >
                        <span className="w-5 h-5 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center font-bold text-[11px] flex-shrink-0 mt-0.5">
                          {idx + 1}
                        </span>
                        <span>{point}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Spanduk Bantuan Langsung */}
        <section className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-brand-900 to-brand-800 rounded-3xl p-8 sm:p-10 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-xl text-center md:text-left">
              <span className="inline-flex items-center gap-1.5 bg-white/10 text-gold-300 font-semibold text-xs px-3 py-1 rounded-full mb-3">
                <HelpCircle size={14} />
                Bantuan & Konsultasi Langsung
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold mb-2">
                Mengalami Kendala Saat Pemasangan?
              </h3>
              <p className="text-white/70 text-sm sm:text-base leading-relaxed">
                Tim bantuan teknis Sistem IPL siap mendampingi Anda jika membutuhkan panduan langsung saat
                mengaktifkan izin sumber tidak dikenal maupun memasang berkas APK di perangkat Anda.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto flex-shrink-0">
              <a
                href="https://wa.me/6287886815136"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-gold-500 hover:bg-gold-600 text-brand-950 font-bold px-6 py-3 rounded-xl transition-all shadow-md text-sm"
              >
                <MessageCircle size={18} />
                Hubungi Bantuan WhatsApp
              </a>
              <Link to="/download">
                <Button
                  variant="outline"
                  className="w-full sm:w-auto border-white/20 text-white hover:bg-white/10 hover:text-white font-semibold px-6 py-3 h-auto text-sm"
                >
                  <Download size={16} className="mr-1.5" />
                  Unduh Berkas APK
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};
